import apiClient from '../config/api';
import { LegalInfo, LegalInfoDTO } from '../types/legalInfo.types';

/**
 * Service de gestion des informations légales
 * Gère les mentions légales obligatoires pour les factures d'artisans du bâtiment
 */
class LegalInfoService {
  /**
   * Récupérer les informations légales de l'utilisateur connecté
   * @returns Les informations légales ou null si non renseignées
   */
  async getMyLegalInfo(): Promise<LegalInfo | null> {
    try {
      console.log('📋 Récupération des informations légales...');
      const response = await apiClient.get<LegalInfo>('/api/users/me/legal-info');
      console.log('✅ Informations légales récupérées:', response.data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log('ℹ️ Aucune information légale trouvée');
        return null;
      }
      console.error('❌ Erreur lors de la récupération des informations légales:', error);
      throw error;
    }
  }

  /**
   * Créer ou mettre à jour les informations légales de l'utilisateur connecté
   * @param legalInfoDTO - Les informations légales à enregistrer
   * @returns Les informations légales mises à jour
   */
  async updateMyLegalInfo(legalInfoDTO: LegalInfoDTO): Promise<LegalInfo> {
    try {
      console.log('💾 Mise à jour des informations légales:', legalInfoDTO);
      const response = await apiClient.put<LegalInfo>('/api/users/me/legal-info', legalInfoDTO);
      console.log('✅ Informations légales mises à jour avec succès');
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la mise à jour des informations légales:', error);
      throw error;
    }
  }

  /**
   * Supprimer les informations légales de l'utilisateur connecté
   */
  async deleteMyLegalInfo(): Promise<void> {
    try {
      console.log('🗑️ Suppression des informations légales...');
      await apiClient.delete('/api/users/me/legal-info');
      console.log('✅ Informations légales supprimées avec succès');
    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression des informations légales:', error);
      throw error;
    }
  }

  /**
   * Vérifier si les informations légales obligatoires sont complètes
   * @param legalInfo - Les informations légales à vérifier
   * @returns true si toutes les informations obligatoires sont renseignées
   */
  isComplete(legalInfo: LegalInfo | null): boolean {
    if (!legalInfo) return false;

    // Vérification des champs obligatoires
    const required = [
      legalInfo.assuranceNom,
      legalInfo.assuranceNumeroPolice,
      legalInfo.assuranceCouvertureGeographique,
      legalInfo.assuranceDateValidite,
      legalInfo.formeJuridique,
      legalInfo.delaiPaiementJours,
      legalInfo.tauxPenalitesRetard,
      legalInfo.indemniteForfaitaireRecouvrement,
      legalInfo.mediateurConsommation,
    ];

    // Au moins RCS ou RM doit être renseigné
    const hasIdentification = !!(legalInfo.rcs || legalInfo.rm);

    return required.every((field) => field !== null && field !== undefined && field !== '') && hasIdentification;
  }

  /**
   * Obtenir la liste des champs manquants
   * @param legalInfo - Les informations légales à vérifier
   * @returns Liste des noms de champs manquants
   */
  getMissingFields(legalInfo: LegalInfo | null): string[] {
    const missing: string[] = [];

    if (!legalInfo) {
      return ['Toutes les informations légales'];
    }

    if (!legalInfo.assuranceNom) missing.push('Nom de l\'assurance décennale');
    if (!legalInfo.assuranceNumeroPolice) missing.push('Numéro de police d\'assurance décennale');
    if (!legalInfo.assuranceCouvertureGeographique) missing.push('Couverture géographique');
    if (!legalInfo.assuranceDateValidite) missing.push('Date de validité de l\'assurance');
    if (!legalInfo.formeJuridique) missing.push('Forme juridique');
    if (!legalInfo.rcs && !legalInfo.rm) missing.push('RCS ou RM');
    if (!legalInfo.delaiPaiementJours) missing.push('Délai de paiement');
    if (!legalInfo.tauxPenalitesRetard) missing.push('Taux de pénalités de retard');
    if (!legalInfo.indemniteForfaitaireRecouvrement) missing.push('Indemnité forfaitaire de recouvrement');
    if (!legalInfo.mediateurConsommation) missing.push('Médiateur de la consommation');

    return missing;
  }

  /**
   * Valider un IBAN
   * @param iban - IBAN à valider
   * @returns true si l'IBAN est valide
   */
  validateIBAN(iban: string | undefined): boolean {
    if (!iban) return false;

    // Supprimer les espaces
    const cleanIBAN = iban.replace(/\s/g, '');

    // Vérifier la longueur (entre 15 et 34 caractères)
    if (cleanIBAN.length < 15 || cleanIBAN.length > 34) {
      return false;
    }

    // Vérifier que cela commence par 2 lettres (code pays)
    const countryCode = cleanIBAN.substring(0, 2);
    if (!/^[A-Z]{2}/.test(countryCode)) {
      return false;
    }

    // IBAN français : FR + 2 chiffres de contrôle + 23 caractères
    if (countryCode === 'FR' && cleanIBAN.length !== 27) {
      return false;
    }

    return true;
  }

  /**
   * Formater un IBAN avec des espaces tous les 4 caractères
   * @param iban - IBAN à formater
   * @returns IBAN formaté
   */
  formatIBAN(iban: string | undefined): string {
    if (!iban) return '';

    const cleanIBAN = iban.replace(/\s/g, '');
    return cleanIBAN.match(/.{1,4}/g)?.join(' ') || cleanIBAN;
  }

  /**
   * Valider un BIC
   * @param bic - BIC à valider
   * @returns true si le BIC est valide
   */
  validateBIC(bic: string | undefined): boolean {
    if (!bic) return false;

    // BIC : 8 ou 11 caractères alphanumériques
    return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic.toUpperCase());
  }
}

export default new LegalInfoService();
